import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import {
  getOrderDetail,
  releaseHold,
  releaseAllHolds,
  reapplyHold,
  prefillAllocation,
  submitAllocation,
  verifyAllocation,
  getAuditTimeline,
  extractApiError,
} from '../../api/holdallocation/hold-allocation-api'

const initialState = {
  orderDetail: null,
  holds: [],
  allocationLines: [],
  auditEvents: [],
  isFetching: false,
  isSubmitting: false,
  isVerifying: false,
  error: null,
  submitError: null,
  releaseAllSummary: null,
  // Allocation verify state — populated after submit completes.
  // Includes ppsCount, ppsCreated, ppsStatus, isScopeToggleNeeded, lines[]
  allocationVerify: null,
}

// ~7s async PPS creation window (H114) — wait a bit longer for safety margin.
const VERIFY_DELAY_MS = 8000

export const fetchOrderDetail = createAsyncThunk(
  'holdAllocationDetail/fetchOrderDetail',
  async (arg, { rejectWithValue }) => {
    try {
      // Support both string (orderId) for v1 and { orderId, role } for v2
      const orderId = typeof arg === 'string' ? arg : arg.orderId
      const role = typeof arg === 'string' ? 'CUSTOMER_SERVICE' : arg.role || 'CUSTOMER_SERVICE'
      const org = typeof arg === 'object' ? arg.org || '' : ''
      const res = await getOrderDetail(orderId, role, org)
      return res.data.data
    } catch (e) {
      return rejectWithValue(extractApiError(e))
    }
  },
)

export const doReleaseHold = createAsyncThunk(
  'holdAllocationDetail/doReleaseHold',
  async ({ orderId, holdId, role, username, comment }, { rejectWithValue }) => {
    try {
      const res = await releaseHold(orderId, holdId, role, username, { releaseComment: comment })
      return res.data.data
    } catch (e) {
      return rejectWithValue(extractApiError(e))
    }
  },
)

export const doReleaseAll = createAsyncThunk(
  'holdAllocationDetail/doReleaseAll',
  async ({ orderId, role, username, comment }, { rejectWithValue }) => {
    try {
      const res = await releaseAllHolds(orderId, role, username, { releaseComment: comment })
      const body = res.data || {}
      const data = body.data || {}
      const items = Array.isArray(data.items) ? data.items : []
      const released =
        typeof data.released === 'number'
          ? data.released
          : items.filter((it) => it.status === 'RELEASED').length
      const failed =
        typeof data.failed === 'number'
          ? data.failed
          : items.filter((it) => it.status === 'FAILED').length
      const locked =
        typeof data.locked === 'number'
          ? data.locked
          : items.filter((it) => it.status === 'LOCKED').length
      const partial =
        res.status === 207 || body.errorCode === 'PARTIAL_RELEASE' || failed > 0 || locked > 0
      return {
        items,
        released,
        failed,
        locked,
        partial,
        message: body.message || '',
      }
    } catch (e) {
      return rejectWithValue(extractApiError(e))
    }
  },
)

export const doReapplyHold = createAsyncThunk(
  'holdAllocationDetail/doReapplyHold',
  async ({ orderId, holdId, holdCode, applyComment, role, username }, { rejectWithValue }) => {
    try {
      const body = { holdCode }
      if (applyComment) body.applyComment = applyComment
      const res = await reapplyHold(orderId, holdId, role, username, body)
      return res.data.data
    } catch (e) {
      return rejectWithValue(extractApiError(e))
    }
  },
)

export const doPrefillAllocation = createAsyncThunk(
  'holdAllocationDetail/doPrefillAllocation',
  async ({ orderId, org } = {}, { rejectWithValue }) => {
    try {
      // support both string (orderId) for backward compat and { orderId, org }
      const id = typeof orderId === 'string' ? orderId : orderId
      const o = org || (typeof org === 'string' ? org : '')
      const res = await prefillAllocation(id, o)
      return res.data.data
    } catch (e) {
      return rejectWithValue(extractApiError(e))
    }
  },
)

export const doSubmitAllocation = createAsyncThunk(
  'holdAllocationDetail/doSubmitAllocation',
  async ({ orderId, role, username, lines, org }, { rejectWithValue }) => {
    try {
      const res = await submitAllocation(orderId, role, username, { lines }, org)
      return res.data.data
    } catch (e) {
      return rejectWithValue(extractApiError(e))
    }
  },
)

/**
 * Verify allocation/PPS state after the async PPS creation window.
 * Pass `ppsCountBefore` from the submit response to detect newly-created PPS.
 */
export const doVerifyAllocation = createAsyncThunk(
  'holdAllocationDetail/doVerifyAllocation',
  async ({ orderId, ppsCountBefore, lineNumber }, { rejectWithValue }) => {
    try {
      const res = await verifyAllocation(orderId, ppsCountBefore, lineNumber)
      return res.data.data
    } catch (e) {
      return rejectWithValue(extractApiError(e))
    }
  },
)

/**
 * Submit allocation, then after the async PPS creation window call verify.
 *
 * If verify reports `isScopeToggleNeeded=true`, the order is stuck at
 * Scope=1 (post-alloc Scope→2 toggle silently failed). Recover by re-submitting
 * with the **current** committed values (no-op allocation) — this re-triggers
 * the post-alloc scope toggle path in `submitAllocation`. No new endpoint
 * needed; the existing submit endpoint is the recovery mechanism.
 */
// Promise-wrapped sleep helper — keeps the executor pure (no return-from-executor lint).
const sleep = (ms) =>
  new Promise((resolve) => {
    setTimeout(resolve, ms)
  })

export const doSubmitAndVerifyAllocation = createAsyncThunk(
  'holdAllocationDetail/doSubmitAndVerifyAllocation',
  async ({ orderId, role, username, lines, org }, { rejectWithValue }) => {
    try {
      // Step 1: submit
      const submitRes = await submitAllocation(orderId, role, username, { lines }, org)
      const submitData = submitRes.data.data || {}
      const { ppsCountBefore } = submitData

      // Step 2: wait for async PPS creation window (~7s, H114)
      await sleep(VERIFY_DELAY_MS)

      // Step 3: verify
      const verifyRes = await verifyAllocation(orderId, ppsCountBefore)
      let verifyData = verifyRes.data.data || {}

      // Step 4: scope-toggle recovery — if order is stuck at Scope=1, re-submit
      // with current committed values (no-op alloc) to retry post-alloc Scope→2.
      if (verifyData.isScopeToggleNeeded && Array.isArray(verifyData.lines)) {
        const recoveryLines = verifyData.lines
          .filter((l) => l.committedQty > 0)
          .map((l) => ({ lineNumber: l.lineNumber, allocateQty: l.committedQty }))
        if (recoveryLines.length > 0) {
          await submitAllocation(orderId, role, username, { lines: recoveryLines }, org)
          await sleep(VERIFY_DELAY_MS)
          const recoveryVerifyRes = await verifyAllocation(orderId, ppsCountBefore)
          verifyData = recoveryVerifyRes.data.data || verifyData
        }
      }

      return { submit: submitData, verify: verifyData }
    } catch (e) {
      return rejectWithValue(extractApiError(e))
    }
  },
)

export const fetchAuditTimeline = createAsyncThunk(
  'holdAllocationDetail/fetchAuditTimeline',
  async (orderId, { rejectWithValue }) => {
    try {
      const res = await getAuditTimeline(orderId)
      return res.data.data
    } catch (e) {
      return rejectWithValue(extractApiError(e))
    }
  },
)

const holdAllocationDetailSlice = createSlice({
  name: 'holdAllocationDetail',
  initialState,
  reducers: {
    setAllocationQty(state, action) {
      const { lineNumber, qty } = action.payload
      const line = state.allocationLines.find((l) => l.lineNumber === lineNumber)
      if (line) line.allocateQty = qty
    },
    resetDetail() {
      return initialState
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchOrderDetail
      .addCase(fetchOrderDetail.pending, (state) => {
        state.isFetching = true
        state.error = null
        state.orderDetail = null
        state.holds = []
        state.allocationLines = []
        state.auditEvents = []
      })
      .addCase(fetchOrderDetail.fulfilled, (state, action) => {
        state.isFetching = false
        state.orderDetail = action.payload
        state.holds = action.payload.holds || []
        state.allocationLines = action.payload.allocationLines || []
      })
      .addCase(fetchOrderDetail.rejected, (state, action) => {
        state.isFetching = false
        state.error = action.payload
      })
      // doReleaseHold
      .addCase(doReleaseHold.pending, (state) => {
        state.isSubmitting = true
        state.submitError = null
      })
      .addCase(doReleaseHold.fulfilled, (state, action) => {
        state.isSubmitting = false
        // Update hold as released in local state
        const updated = action.payload
        if (updated) {
          const idx = state.holds.findIndex((h) => h.holdId === updated.holdId)
          if (idx !== -1) state.holds[idx] = updated
        }
      })
      .addCase(doReleaseHold.rejected, (state, action) => {
        state.isSubmitting = false
        state.submitError = action.payload
      })
      // doReleaseAll
      .addCase(doReleaseAll.pending, (state) => {
        state.isSubmitting = true
        state.submitError = null
        state.releaseAllSummary = null
      })
      .addCase(doReleaseAll.fulfilled, (state, action) => {
        state.isSubmitting = false
        const { items = [] } = action.payload || {}
        // Patch only rows that came back as RELEASED. LOCKED/FAILED stay unchanged
        // until the follow-up fetchOrderDetail refreshes authoritative state.
        if (items.length) {
          const releasedIds = new Set(
            items.filter((it) => it.status === 'RELEASED').map((it) => it.holdId),
          )
          state.holds = state.holds.map((h) =>
            releasedIds.has(h.holdId) ? { ...h, released: true } : h,
          )
        }
        state.releaseAllSummary = action.payload || null
      })
      .addCase(doReleaseAll.rejected, (state, action) => {
        state.isSubmitting = false
        state.submitError = action.payload
      })
      // doReapplyHold
      .addCase(doReapplyHold.pending, (state) => {
        state.isSubmitting = true
        state.submitError = null
      })
      .addCase(doReapplyHold.fulfilled, (state, action) => {
        state.isSubmitting = false
        const updated = action.payload
        if (updated) {
          const idx = state.holds.findIndex((h) => h.holdId === updated.holdId)
          if (idx !== -1) state.holds[idx] = updated
        }
      })
      .addCase(doReapplyHold.rejected, (state, action) => {
        state.isSubmitting = false
        state.submitError = action.payload
      })
      // doPrefillAllocation
      .addCase(doPrefillAllocation.pending, (state) => {
        state.isSubmitting = true
      })
      .addCase(doPrefillAllocation.fulfilled, (state, action) => {
        state.isSubmitting = false
        if (action.payload) state.allocationLines = action.payload
      })
      .addCase(doPrefillAllocation.rejected, (state, action) => {
        state.isSubmitting = false
        state.submitError = action.payload
      })
      // doSubmitAllocation
      .addCase(doSubmitAllocation.pending, (state) => {
        state.isSubmitting = true
        state.submitError = null
      })
      .addCase(doSubmitAllocation.fulfilled, (state) => {
        state.isSubmitting = false
      })
      .addCase(doSubmitAllocation.rejected, (state, action) => {
        state.isSubmitting = false
        state.submitError = action.payload
      })
      // doVerifyAllocation — read-only; updates allocationVerify snapshot
      .addCase(doVerifyAllocation.pending, (state) => {
        state.isVerifying = true
      })
      .addCase(doVerifyAllocation.fulfilled, (state, action) => {
        state.isVerifying = false
        state.allocationVerify = action.payload || null
      })
      .addCase(doVerifyAllocation.rejected, (state) => {
        state.isVerifying = false
      })
      // doSubmitAndVerifyAllocation — composite: submit + wait + verify (+ recovery)
      .addCase(doSubmitAndVerifyAllocation.pending, (state) => {
        state.isSubmitting = true
        state.isVerifying = true
        state.submitError = null
        state.allocationVerify = null
      })
      .addCase(doSubmitAndVerifyAllocation.fulfilled, (state, action) => {
        state.isSubmitting = false
        state.isVerifying = false
        state.allocationVerify = (action.payload && action.payload.verify) || null
      })
      .addCase(doSubmitAndVerifyAllocation.rejected, (state, action) => {
        state.isSubmitting = false
        state.isVerifying = false
        state.submitError = action.payload
      })
      // fetchAuditTimeline
      .addCase(fetchAuditTimeline.pending, (state) => {
        state.auditEvents = []
      })
      .addCase(fetchAuditTimeline.fulfilled, (state, action) => {
        state.auditEvents = action.payload || []
      })
      .addCase(fetchAuditTimeline.rejected, (state) => {
        state.auditEvents = []
      })
  },
})

export const { setAllocationQty, resetDetail } = holdAllocationDetailSlice.actions

export default holdAllocationDetailSlice.reducer
